import React, { useCallback, useLayoutEffect, useMemo } from 'react';
import { TouchableHighlight, View } from 'react-native';
import Animated from 'react-native-reanimated';
import { addMonths, addWeeks, format, startOfDay } from 'date-fns';
import { AppView, CardView, Spacer } from 'design-system';
import Button, { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import RadioButton from 'design-system/RadioButton';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import Text from 'design-system/Text';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import { muteAll, useFetchOld } from 'hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import createStyleSheets from 'utils/createStyleSheets';
import { editAutosaveUpcomingPayment, editSavingsPlan } from '../actions';
import { PauseAutosaveExpiryDateType } from '../types';
import { formatPauseAutosaveDate } from '../utils';
export const strings = {
    upcomingPayment: 'Your upcoming payment will be processed unless skipped.',
    pendingPayment: 'Your pending payment will be processed as usual.',
};
const Row = ({ title, subTitle, isChecked, onPress }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    return (<TouchableHighlight onPress={onPress} underlayColor={colors.background.underlay} activeOpacity={1}>
      <View style={styles.row}>
        <View style={styles.flex}>
          <Text Text-16>{title}</Text>
          <Text TextThin-14 Secondary>
            {subTitle}
          </Text>
        </View>
        <Spacer w={16}/>
        <RadioButton type="single" isChecked={isChecked}/>
      </View>
    </TouchableHighlight>);
};
const fetchOptions = {
    ...muteAll,
    throws: true,
    muteFetching: false,
};
const PauseAutosaveInfoScreen = withAnimatedNavigationHeader(({ scrollHandler, route, navigation }) => {
    const { autoSaveId, paymentToSkip, processingActivity } = route.params;
    const [, , fetching, fetch] = useFetchOld(fetchOptions);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton />,
        });
    }, []);
    const date = useMemo(() => startOfDay(new Date()), []);
    const [pauseSelection, setPauseSelection] = React.useState(undefined);
    const { paddingHorizontalStyle } = useAppFrameDimensions();
    const contentContainerStyles = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    const data = useMemo(() => [
        {
            title: 'A week',
            finalDate: addWeeks(date, 1),
            type: PauseAutosaveExpiryDateType.ONE_WEEK,
            subTitle: `Until ${format(addWeeks(date, 1), `EEEE d LLLL`)}`,
        },
        {
            title: '2 weeks',
            finalDate: addWeeks(date, 2),
            type: PauseAutosaveExpiryDateType.TWO_WEEKS,
            subTitle: `Until ${format(addWeeks(date, 2), `EEEE d LLLL`)}`,
        },
        {
            title: 'A month',
            finalDate: addMonths(date, 1),
            type: PauseAutosaveExpiryDateType.MONTH,
            subTitle: `Until ${format(addMonths(date, 1), `EEEE d LLLL`)}`,
        },
        {
            title: 'Other',
            finalDate: undefined,
            subTitle: 'Choose the date',
            type: PauseAutosaveExpiryDateType.CUSTOM,
        },
        {
            finalDate: date,
            title: 'Pause autosave',
            type: PauseAutosaveExpiryDateType.NO_LIMIT,
            subTitle: 'Collections will stop until you turn them back on',
        },
    ], [date]);
    const onSelectOption = useCallback((selection) => () => {
        setPauseSelection(selection);
    }, []);
    const handleSave = useCallback(async () => {
        if (pauseSelection?.type === PauseAutosaveExpiryDateType.CUSTOM) {
            navigation.navigateDeprecated('PauseAutosaveSelectDateScreen', {
                autoSaveId,
                paymentToSkip,
                processingActivity,
            });
            return;
        }
        if (pauseSelection?.finalDate) {
            try {
                const pausedUntil = pauseSelection?.type === PauseAutosaveExpiryDateType.NO_LIMIT
                    ? undefined
                    : pauseSelection.finalDate.toISOString();
                await fetch(editSavingsPlan(autoSaveId, {
                    pausedUntil,
                    isPaused: true,
                }));
                if (paymentToSkip) {
                    await fetch(editAutosaveUpcomingPayment(paymentToSkip.id, true));
                }
                const noLimitSubtitle = 'Your autosave is paused. Collections will start again if you turn the feature back on.';
                navigation.navigateDeprecated('Success', {
                    title: 'Success',
                    subTitle: pauseSelection.type === PauseAutosaveExpiryDateType.NO_LIMIT ? (`${noLimitSubtitle}${processingActivity ? `\n\n${strings.pendingPayment}` : ''}`) : (<Text TextThin-16 Secondary>
                  Your autosave is on hold until{' '}
                  <Text Text-16 Secondary>
                    {formatPauseAutosaveDate(pauseSelection.finalDate)}
                  </Text>
                  . I’ll notify you when I start setting money aside again.
                  {`\n\n${processingActivity ? strings.pendingPayment : strings.upcomingPayment}`}
                </Text>),
                    onContinue: () => {
                        navigation.pop(2);
                        return true;
                    },
                    buttonText: 'Got it',
                });
            }
            catch {
                //
            }
        }
    }, [autoSaveId, pauseSelection, processingActivity, paymentToSkip]);
    return (<AppView type="secondary" withFrame={false}>
        <Animated.ScrollView scrollEventThrottle={16} onScroll={scrollHandler} contentContainerStyle={contentContainerStyles} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
          <ScreenInfoHeader title="How long would you like to pause for?" subTitle="You’ll get a notification when we resume setting money aside"/>
          <CardView noMarginTop noMarginBottom>
            {data.map((eachData) => (<Row key={eachData.type} title={eachData.title} subTitle={eachData.subTitle} onPress={onSelectOption(eachData)} isChecked={eachData.type === pauseSelection?.type}/>))}
          </CardView>
        </Animated.ScrollView>
        <Button floating brand title="Save" isFetching={fetching} disabled={!pauseSelection} onPress={handleSave}/>
      </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styleSet = createStyleSheets(() => ({
    row: {
        alignItems: 'center',
        flexDirection: 'row',
        paddingVertical: rem(20),
        paddingHorizontal: rem(16),
    },
    flex: {
        flex: 1,
    },
}));
export default PauseAutosaveInfoScreen;
